import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './MeadowCaseCard.css';
import { isFigmaCapture } from '../../utils/figmaCapture';
import QrPayFilm from '../QrPayFilm/QrPayFilm';

/*
  Live HTML teasers for the case cards (cut from each case study's hero film).
  Mounted only when the card nears the viewport; they play while the card is
  hovered or keyboard-focused (or, on touch screens, mostly on screen) and rewind on leave.
*/
const useCardHover = () => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const card = el.closest('.home-case') || el;
    // A swipe also fires pointerleave, so touch pointers never drive `active`
    const onPointer = (value) => (e) => { if (e.pointerType !== 'touch') setActive(value); };
    const enter = onPointer(true);
    const leave = onPointer(false);
    const on = () => setActive(true);
    const off = () => setActive(false);
    card.addEventListener('pointerenter', enter); card.addEventListener('pointerleave', leave);
    card.addEventListener('focusin', on); card.addEventListener('focusout', off);
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setNear(true); }, { rootMargin: '400px 0px' });
    io.observe(el);
    // Touch screens have no hover: play while the card is mostly on screen instead
    const touch = window.matchMedia?.('(hover: none)').matches;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const seen = touch && !reduce ? new IntersectionObserver(([e]) => setActive(e.intersectionRatio >= 0.6), { threshold: [0, 0.6] }) : null;
    if (seen) seen.observe(el);
    return () => {
      if (seen) seen.disconnect();
      card.removeEventListener('pointerenter', enter); card.removeEventListener('pointerleave', leave);
      card.removeEventListener('focusin', on); card.removeEventListener('focusout', off);
      io.disconnect();
    };
  }, []);
  return [ref, near, active];
};

const QrTeaser = ({ teaser }) => {
  const [ref, , active] = useCardHover();
  return (
    <div ref={ref} className="home-case__device home-case__device--film">
      <QrPayFilm teaser={teaser} active={active} />
    </div>
  );
};

const FilmFrameTeaser = ({ src, poster }) => {
  const [ref, near, active] = useCardHover();
  const frame = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (loaded) frame.current?.contentWindow?.postMessage({ kitFilm: active ? 'play' : 'stop' }, '*');
  }, [active, loaded]);
  return (
    <div ref={ref} className="home-case__film-frame">
      <img className="home-case__media" src={poster} alt="" loading="eager" decoding="async" style={{ opacity: loaded ? 0 : 1 }} />
      {near ? (
        <iframe ref={frame} src={src} title="Case study teaser" tabIndex={-1} aria-hidden="true" loading="lazy" onLoad={() => setTimeout(() => setLoaded(true), 900)} />
      ) : null}
    </div>
  );
};


/** Device media used on the landing case grid (and case-study NEXT). */
export const MeadowCaseMedia = ({ project }) => {
  if (!project) {
    return <div className="home-case__screen home-case__screen--empty" />;
  }

  const {
    image,
    imageAlt,
    coverPoster,
    coverLayout,
    coverFilm,
    coverTeaser,
  } = project;
  const poster = coverPoster || image;
  const isTablet = coverLayout === 'ditto-tablet';
  const isDesktop =
    coverLayout === 'oracle-dashboard' || coverLayout === 'kit-desktop';

  if (coverLayout === 'qr-film' && coverTeaser && !isFigmaCapture) {
    return <QrTeaser teaser={coverTeaser} />;
  }

  const media = coverFilm && !isFigmaCapture ? (
    <FilmFrameTeaser src={coverFilm} poster={poster} />
  ) : (
    <img
      className="home-case__media"
      src={poster || image}
      alt={imageAlt || ''}
      loading="eager"
      decoding="async"
      fetchPriority="high"
    />
  );

  if (coverLayout === 'ditto-duo' && coverFilm && !isFigmaCapture) {
    return (
      <div className="home-case__device home-case__device--film home-case__device--duo">
        {media}
      </div>
    );
  }

  if (isTablet) {
    return (
      <div className="home-case__device home-case__device--tablet">
        <div className="home-case__screen home-case__screen--ditto-tablet">
          {media}
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        'home-case__device',
        isDesktop && 'home-case__device--desktop',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isDesktop ? (
        <div className="home-case__chrome" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      ) : null}
      <div
        className={[
          'home-case__screen',
          coverLayout && `home-case__screen--${coverLayout}`,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {media}
      </div>
    </div>
  );
};

/**
 * Landing-style case tile: paint plate + device thumb + sans title.
 * `item` matches meadowCases entries: { title, project, tone }.
 */
const MeadowCaseCard = ({ item, index = 0 }) => {
  const { title, project, tone } = item;
  const slug = project?.slug;
  const comingSoon = project?.comingSoon || !slug;
  const Wrapper = comingSoon ? 'div' : Link;
  const wrapperProps = comingSoon
    ? { role: 'group', 'aria-label': `${title} (coming soon)` }
    : {
        to: `/work/${slug}`,
        'aria-label': title,
      };

  return (
    <Wrapper
      {...wrapperProps}
      className={[
        'home-case',
        'home-case--plain',
        tone && `home-case--tone-${tone}`,
        comingSoon && 'home-case--soon',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--case-i': index }}
    >
      <div className="home-case__thumb">
        <MeadowCaseMedia project={project} />
      </div>
      <h3 className="home-case__title">{title}</h3>
      {item.meta ? <p className="home-case__meta">{item.meta}</p> : null}
    </Wrapper>
  );
};

export default MeadowCaseCard;
